import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppState } from '../../app/state';
import { computeCompletionStats, computeDashboardStats, type CoverageSlice } from '../../lib/stats';
import { DOC_BEAN_COLOR, DOC_KINDS, PIPELINE_DISPLAY } from '../../lib/docs';
import { buildHiddenSets, canAiAdmin, canViewLogs, canViewTodos, isPermHidden } from '../../lib/permissions';
import { canAiAdminHere } from '../../lib/capabilities';
import { inEditMode } from '../../lib/mode';
import { LanguagePicker } from '../../components/LanguagePicker';
import type { DashboardScreen } from '../../lib/location';
import type { NodeId } from '../../lib/types';
import { registerHandle } from '../../app/handles';
import { reportAdminScreen } from '../../app/router';
import { PresenceStack } from '../presence/PresenceStack';
import { DocumentsTab } from './DocumentsTab';
import { AiTab } from './AiTab';
import { LogsTab } from './LogsTab';
import { ProjectsTab } from './ProjectsTab';
import { Pie, R } from './Pie';

/**
 * The documentation dashboard — five tabs since V0.50.2, each with its own address.
 *
 * THE TAB KEY IS THE ADMIN-SCREEN WORD (`lib/location.ts` `DASHBOARD_SCREENS`): `dashboard` is
 * the Overview, `documents` the Documents table, `projects` the Projects tab, `logs` the two
 * logs, `ai-docs` the AI generation console. So `reportAdminScreen(tab, open)` IS the URL, there
 * is no mapping table to keep in step, and the router treats a change between two of these
 * words as a tab switch rather than a close-and-reopen. That effect has NO cleanup on purpose:
 * reporting the old word closed before the new one open would push a transient map URL between
 * two tab URLs.
 *
 * THE PROJECTS TAB (V0.50.2) is the second read surface reserved to a role rather than to edit
 * mode: drawn for `VIEW_TODOS`, reached without the Edit toggle like Logs, because the to-do
 * manager it is for may hold no editing brick at all. Its one verb — ticking a to-do — is the
 * popup's own route and gate, not a dashboard decision.
 *
 * IT REMEMBERS THE LAST TAB. The region stays mounted while closed (it returns null), so `tab`
 * survives close/open; the handle's `open(tab?)` sets it only when a tab is named — a deep link,
 * or a header button that means a specific tab — and the topbar's plain open lands where you
 * were. A fresh page starts on the Overview, which is what the deep link `/admin/dashboard` and
 * two specs (`08-chrome`, `20-dashboard`) expect: they open the dashboard and look for
 * `#dash-grid` at once. The tab bar is `.perm-tab`, never `.dash-tile` — `20-dashboard` counts
 * the tiles by class.
 *
 * THE TABS HAND EACH OTHER A NODE. A Documents row's "Configure" lands on the AI tab narrowed to
 * that node; an AI row's "See WF state" lands on the Documents tab narrowed to that node's pages.
 * `focus` is that hand-off: set with the tab, read ONCE by the tab as its initial state (the
 * mount is keyed on it, so a repeated hand-off remounts), cleared by any plain tab click.
 *
 * ONLY THE SELECTED TAB IS MOUNTED, so a fetch runs when somebody actually looks, and the AI
 * console's unmount cleanup (stop polling, flush prompts) runs on every way out — tab switch,
 * close button, Escape, the router. The AI tab is DRAWN only for `canAiAdminHere && editing`, the
 * console's own two-gate rule; if the selected tab stops being allowed (a permission edit), the
 * Overview takes over rather than an empty pane.
 *
 * ESCAPE closes the dashboard — unless a document or the AI context screen sits above it, each
 * with its own Escape: one key press must not close two surfaces.
 *
 * OVERVIEW puts `lib/stats.ts` into production — the same functions `e2e/15-core-parity.spec.ts`
 * proved reproduce `computeDashboardStats()` and `computeCompletionStats()` exactly, field for
 * field, on the real map. Nothing here recomputes anything. The two figures are counted
 * differently ON PURPOSE: the tallies are absolute — every node the user can see — and the
 * completion pies EXCLUDE EXEMPT nodes (documentation plan = {}, "nothing expected"), because a
 * node deliberately marked as never needing documentation would otherwise drag the percentage
 * down for ever and no amount of work would move it.
 */

/** How long the header's language picker keeps its "I moved" ring. */
const PULSE_MS = 700;

const PieTile = ({ pct, color, label, sub }: { pct: number; color: string; label: string; sub?: string }) => (
  <div className="dash-tile dash-pie-tile">
    <Pie pct={pct} color={color} />
    <div className="dash-label">{label}</div>
    {sub ? <div className="dash-pie-sub">{sub}</div> : null}
  </div>
);

const Tile = ({ num, label }: { num: string | number; label: string }) => (
  <div className="dash-tile">
    <div className="dash-num">{num}</div>
    <div className="dash-label">{label}</div>
  </div>
);

const Section = ({ label }: { label: string }) => <div className="dash-section">{label}</div>;

const StatusTile = ({ num, label, color }: { num: number; label: string; color: string }) => (
  <div className="dash-tile" style={{ borderLeft: `3px solid ${color}` }}>
    <div className="dash-num" style={{ color }}>
      {num}
    </div>
    <div className="dash-label">{label}</div>
  </div>
);

/**
 * The pipeline-stage distribution, as one multi-segment donut.
 *
 * Each segment is a full circle with a dash array of "visible gap", rotated by an offset that
 * accumulates as the segments are laid down — the standard trick for a donut without any
 * arc-path maths. The offset is taken modulo 100 twice because it can go negative once the
 * accumulated total passes 25.
 */
function StatusDonut({ by }: { by: Record<string, number> }) {
  const total = PIPELINE_DISPLAY.reduce((a, s) => a + (by[s.key] || 0), 0);
  let cum = 0;
  const segs = total
    ? PIPELINE_DISPLAY.map((s) => {
        const v = by[s.key] || 0;
        if (!v) return null;
        const p = (v / total) * 100;
        const off = (((25 - cum) % 100) + 100) % 100;
        cum += p;
        return (
          <circle
            key={s.key}
            cx="18"
            cy="18"
            r={R}
            fill="none"
            stroke={s.color}
            strokeWidth="4"
            strokeDasharray={`${p.toFixed(2)} ${(100 - p).toFixed(2)}`}
            strokeDashoffset={off.toFixed(2)}
            transform="rotate(-90 18 18)"
          />
        );
      })
    : null;

  return (
    <div className="dash-tile dash-pie-tile" style={{ gridColumn: '1/-1' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 26,
          flexWrap: 'wrap',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <div>
          <svg viewBox="0 0 36 36" width="120" height="120">
            <circle cx="18" cy="18" r={R} fill="none" stroke="var(--line)" strokeWidth="4" opacity="0.4" />
            {segs}
            <text x="18" y="18.6" textAnchor="middle" fontSize="6" fontWeight="600" fill="var(--ink)">
              {total}
            </text>
            <text x="18" y="23.5" textAnchor="middle" fontSize="2.8" fill="var(--ink-dim)">
              nodes
            </text>
          </svg>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 22px', alignItems: 'center' }}>
          {PIPELINE_DISPLAY.map((s) => (
            <span
              key={s.key}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'var(--ink-dim)' }}
            >
              <span style={{ width: 11, height: 11, borderRadius: '50%', flex: '0 0 auto', background: s.color }} />
              {s.label} <b style={{ color: 'var(--ink)' }}>{by[s.key] || 0}</b>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export interface DashboardHandle {
  /** Land on `tab`, or on the tab last shown; returns where it landed, for the URL report.
   *  `opts.logsView` picks which of the Logs tab's two views opens — `openLogs()` passes
   *  'history', because that is what four buttons and spec 25 have always meant by it.
   *  `opts.nodeId` is the `focus` hand-off from outside the region (V0.50.2): the popup's
   *  Progress button opens the Projects tab ON that node's project. */
  open: (tab?: DashboardScreen, opts?: { logsView?: 'actions' | 'history'; nodeId?: NodeId }) => DashboardScreen;
  close: () => void;
}

/** The node one tab hands another, read once by the tab it names. */
type Focus = { tab: DashboardScreen; nodeId: NodeId } | null;

export function Dashboard({ host }: { host: HTMLElement }) {
  const s = useAppState();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<DashboardScreen>('dashboard');
  const [focus, setFocus] = useState<Focus>(null);
  const [pulse, setPulse] = useState(false);
  // Which of the Logs tab's two views to open on. Carried from `openLogs()`, which means
  // the document history, and read once by the tab as its initial state.
  const [logsView, setLogsView] = useState<'actions' | 'history' | undefined>(undefined);
  const tabRef = useRef(tab);
  tabRef.current = tab;
  const pulseTimers = useRef<number[]>([]);

  const aiTabAllowed = canAiAdminHere(s.perm, s.caps) && inEditMode(s);
  /**
   * The Logs tab is drawn for EITHER role, and — unlike every other tab — without edit
   * mode. It is a read surface reserved to a permission, not an editing tool;
   * `openDashboard` carries the same rule as its backstop, and `/admin/logs` has been a
   * shareable address since V0.45.
   */
  const logsTabAllowed = canViewLogs(s.perm) || canAiAdmin(s.perm);
  /** The Projects tab (V0.50.2): the to-do reader's surface, edit mode not required — see the header. */
  const projectsTabAllowed = canViewTodos(s.perm);

  const selectTab = useCallback((t: DashboardScreen, nodeId?: NodeId) => {
    setTab(t);
    setFocus(nodeId ? { tab: t, nodeId } : null);
  }, []);

  useEffect(() => {
    host.classList.toggle('show', open);
  }, [open, host]);

  // The two table tabs are wide; the Overview keeps the shared page width. Toggled on the host
  // (outside every React root) exactly like `show`, so the stylesheet scopes both.
  useEffect(() => {
    host.classList.toggle('wide', open && tab !== 'dashboard');
  }, [open, tab, host]);

  // The URL — the tab word itself. Reported idempotently and WITHOUT a cleanup (see the header):
  // opening pushes the tab, a tab switch pushes the new word over the old one, closing reports
  // whichever word is current. The open/close actions report synchronously too; this catches the
  // tab bar, the internal close button and Escape, none of which go through an action.
  useEffect(() => {
    reportAdminScreen(tab, open);
  }, [open, tab]);

  // A tab that stopped being allowed (a permission edit re-applied) falls back to the Overview.
  useEffect(() => {
    if (tab === 'ai-docs' && !aiTabAllowed) setTab('dashboard');
    if (tab === 'logs' && !logsTabAllowed) setTab('dashboard');
    if (tab === 'projects' && !projectsTabAllowed) setTab('dashboard');
  }, [tab, aiTabAllowed, logsTabAllowed, projectsTabAllowed]);

  useEffect(() => {
    const handle: DashboardHandle = {
      open: (t, opts) => {
        const next = t ?? tabRef.current;
        selectTab(next, opts?.nodeId);
        // Only ever SET by a caller that named one, so a plain open of the Logs tab keeps
        // whichever view the reader last chose.
        if (opts?.logsView) setLogsView(opts.logsView);
        setOpen(true);
        return next;
      },
      close: () => setOpen(false),
    };
    registerHandle('dashboard', handle);
  }, [selectTab]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // A surface above the dashboard takes the key: the document (its own Escape closes it,
      // and its Close reads "Back to Documents" for this very case) and the AI context screen.
      if (s.docOpen) return;
      if (document.getElementById('aidoccfgmodal')?.classList.contains('show')) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, s.docOpen]);

  useEffect(
    () => () => {
      for (const t of pulseTimers.current) window.clearTimeout(t);
    },
    [],
  );

  if (!open) return null;

  // The tables re-read in place, so the picker says "I moved" with an accent ring that settles
  // back. Restarted even if one is running: off this tick, on the next. The locale change
  // itself is the shared picker's (V0.53.5); this is only the ring.
  const pulseOnce = () => {
    setPulse(false);
    pulseTimers.current.push(window.setTimeout(() => setPulse(true), 0));
    pulseTimers.current.push(window.setTimeout(() => setPulse(false), PULSE_MS));
  };

  const hidden = buildHiddenSets(s.perm, s.tagEdges);
  const isHidden = (id: string) => isPermHidden(id, s.edges, hidden);
  const aiAdmin = canAiAdmin(s.perm);

  const stats = computeDashboardStats({
    nodes: s.nodes,
    tags: s.tags,
    links: s.links,
    isPermHidden: isHidden,
    canAiAdmin: aiAdmin,
  });
  const completion = computeCompletionStats({ nodes: s.nodes, tags: s.tags, isPermHidden: isHidden });

  const depths = Object.keys(stats.depthCounts).map(Number).sort((a, b) => a - b);
  const maxDepth = depths.length ? Math.max(...depths) : 0;
  const slice = (b: CoverageSlice) => `${b.count} / ${b.total}`;

  const tabButton = (screen: DashboardScreen, id: string, icon: string, label: string) => (
    <button
      className={tab === screen ? 'perm-tab on' : 'perm-tab'}
      id={id}
      role="tab"
      aria-selected={tab === screen}
      onClick={() => selectTab(screen)}
    >
      <svg className="ico">
        <use href={icon} />
      </svg>
      {label}
    </button>
  );

  const focusFor = (screen: DashboardScreen): NodeId | null => (focus?.tab === screen ? focus.nodeId : null);
  const leave = () => setOpen(false);

  return (
    <>
      <div className="perm-head">
        <h2>
          <svg className="ico">
            <use href="#i-chart" />
          </svg>
          Documentation Coverage Dashboard
        </h2>
        <PresenceStack />
        <LanguagePicker
          id="dash-lang"
          className={pulse ? 'pulse' : undefined}
          title="Language — the tables follow it in place"
          onPicked={pulseOnce}
        />
        <button className="btn" id="dashboard-close" onClick={leave}>
          <svg className="ico">
            <use href="#i-x" />
          </svg>
          <span>Back to InfoStation</span>
        </button>
      </div>
      <div className="perm-wrap">
        <div className="perm-inner">
          <div className="perm-tabs" id="dash-tabs" role="tablist">
            {tabButton('dashboard', 'dash-tab-overview', '#i-chart', 'Overview')}
            {tabButton('documents', 'dash-tab-documents', '#i-doc', 'Documents')}
            {projectsTabAllowed ? tabButton('projects', 'dash-tab-projects', '#i-flag', 'Projects') : null}
            {logsTabAllowed ? tabButton('logs', 'dash-tab-logs', '#i-list', 'Logs') : null}
            {aiTabAllowed ? tabButton('ai-docs', 'dash-tab-ai', '#i-ai', 'AI generation') : null}
          </div>

          {tab === 'documents' ? (
            <DocumentsTab
              key={`documents:${focusFor('documents') ?? ''}`}
              onLeave={leave}
              focusNodeId={focusFor('documents')}
              onConfigure={(nodeId) => selectTab('ai-docs', nodeId)}
            />
          ) : tab === 'projects' && projectsTabAllowed ? (
            <ProjectsTab
              key={`projects:${focusFor('projects') ?? ''}`}
              focusNodeId={focusFor('projects')}
              onLeave={leave}
            />
          ) : tab === 'logs' && logsTabAllowed ? (
            <LogsTab key={`logs:${logsView ?? ''}`} initialView={logsView} onLeave={leave} />
          ) : tab === 'ai-docs' && aiTabAllowed ? (
            <AiTab
              key={`ai-docs:${focusFor('ai-docs') ?? ''}`}
              focusNodeId={focusFor('ai-docs')}
              onLeave={leave}
              onShowInDocuments={(nodeId) => selectTab('documents', nodeId)}
            />
          ) : (
            <div className="dash-grid" id="dash-grid">
              <Section label="Completion" />
              <div className="dash-section-hint">
                {completion.excluded > 0
                  ? `Excludes ${completion.excluded} node${completion.excluded === 1 ? '' : 's'} marked as needing no documentation.`
                  : 'Tip: in a node’s documentation plan, mark it as needing no documentation to exclude it from these percentages.'}
              </div>
              {DOC_KINDS.map((k) => (
                <PieTile
                  key={k.key}
                  pct={completion.byKind[k.key].pct}
                  color={DOC_BEAN_COLOR[k.key]}
                  label={k.label}
                  sub={slice(completion.byKind[k.key])}
                />
              ))}
              <PieTile
                pct={completion.tagsStat.pct}
                color="var(--accent)"
                label="Has tags"
                sub={slice(completion.tagsStat)}
              />

              <Section label="Overview" />
              <Tile num={stats.total} label="Total nodes" />
              <Tile num={stats.withTags} label="Nodes with tags" />
              <Tile num={stats.withDesc} label="Nodes with a description" />

              <Section label="Documentation coverage" />
              {DOC_KINDS.map((k) => (
                <Tile key={k.key} num={stats.byKind[k.key]} label={`Nodes with ${k.label.toLowerCase()}`} />
              ))}
              <Tile num={stats.withCustomDocs} label="Nodes with a custom doc" />
              <Tile num={stats.customDocAttachments} label="Custom doc attachments" />
              <Tile num={stats.distinctCustomDocPages} label="Distinct custom doc pages" />
              <Tile num={stats.fullyDocumented} label="Fully documented nodes" />
              <Tile num={stats.bare} label="Bare nodes (no tags/docs/desc)" />

              <Section label="Tags" />
              <Tile num={stats.avgTagsPerNode.toFixed(2)} label="Average tags per node" />
              <Tile num={`${stats.usedTagCount} / ${stats.definedTagCount}`} label="Tags used / defined" />

              <Section label="Structure" />
              <Tile num={maxDepth} label="Max tree depth" />
              {depths.map((d) => (
                <Tile key={d} num={stats.depthCounts[d]} label={`Nodes at depth ${d}`} />
              ))}
              <Tile num={stats.collapsed} label="Collapsed nodes" />
              <Tile num={stats.crossLinks} label="Cross-links" />

              {aiAdmin ? (
                <>
                  <Section label="Documentation pipeline" />
                  <StatusDonut by={stats.byPipelineStage} />
                  {PIPELINE_DISPLAY.map((v) => (
                    <StatusTile key={v.key} num={stats.byPipelineStage[v.key] || 0} label={v.label} color={v.color} />
                  ))}
                </>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
